<template>
  <div ref="rich-input" class="rich-input" :class="globalTabClasses">
    <div v-if="label" class="rich-label">{{label}}</div>
    <div class="text-editor" :class="{ 'single-line': this.mode == 'input' || this.mode == 'url' }">
      <editor-menu-bubble v-if="mode == 'textarea'" class="menububble" :editor="editor" @hide="hideLinkMenu" v-slot="{ commands, isActive, getMarkAttrs, menu }">
        <div
          class="menububble"
          :class="{ 'is-active': menu.isActive }"
          :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
        >

          <form class="menububble__form" v-if="linkMenuIsActive">
            <div class="d-flex">
              <input class="menububble__input" type="text" v-model="linkUrl" placeholder="https://" ref="linkInput" @keydown.enter.prevent="setLinkUrl(commands.link)" @keydown.esc="hideLinkMenu" @click.stop @input="linkError = ''"/>
              <button class="menububble__button ml-2" type="button" @click.stop="setLinkUrl(commands.link)" :disabled="!linkUrl">
                <v-icon color="white">mdi-check</v-icon>
              </button>
              <button class="menububble__button" @click="removeLink(commands.link)" type="button">
                <v-icon>$remove</v-icon>
              </button>
            </div>
            <div class="menububble__error" v-if="linkError">{{ linkError }}</div>
          </form>

          <template v-else>
            <button class="menububble__button" :class="{ 'is-active': isActive.bold() }" @click="commands.bold">
              <v-icon color="white">mdi-format-bold</v-icon>
            </button>

            <button class="menububble__button" :class="{ 'is-active': isActive.italic() }" @click="commands.italic">
              <v-icon color="white">mdi-format-italic</v-icon>
            </button>

            <button class="menububble__button" :class="{ 'is-active': isActive.underline() }" @click="commands.underline">
              <v-icon color="white">mdi-format-underline</v-icon>
            </button>

            <button
              class="menububble__button"
              @click.stop="showLinkMenu(getMarkAttrs('link'))"
              :class="{ 'is-active': isActive.link() }"
            >
              <span class="text-no-wrap">{{ isActive.link() ? $t('update_link') : ''}}</span>
              <v-icon color="white" v-if="!isActive.link()">mdi-link</v-icon>
            </button>
          </template>
        </div>
      </editor-menu-bubble>
      <editor-content class="editor__content" :class="{ 'disabled': disableInput }" :editor="editor" />
    </div>
    <div class="error-ctn">
      <div v-for="(msg, index) in errorMessages" :key="`error-msg-${index}`">{{msg}}</div>
    </div>
    <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
      <template v-if="hasResults">
        <div
          v-for="(field, index) in filteredItemList"
          :key="field.key"
          class="suggestion-list__item"
          :class="{ 'is-selected': navigatedFieldIndex === index }"
          @click="selectField(field)"
        >
          {{ field.name }}
        </div>
      </template>
      <div v-else class="suggestion-list__item is-empty">
        {{$t('no_match_found')}}
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import { Editor, EditorContent, EditorMenuBubble } from 'tiptap'
import { Mention, Placeholder, Bold, Italic, Link, Underline } from 'tiptap-extensions'
import Hashtag from '@/utils/hashtag.js'
import tippy, { sticky } from 'tippy.js'
import { mapGetters } from 'vuex'
import Doc from '@/utils/customDoc'

export default {
  name: 'RichTextInput',
  props: ['content', 'label', 'placeholder', 'mode', 'json', 'globalTabClasses', 'disableInput', 'disableVariables'],
  components: {
    EditorContent,
    EditorMenuBubble
  },
  computed: {
    ...mapGetters(['findDynamicVariable', 'dynamicVariablesAndScmFields', 'productStcData']),

    hasResults () {
      return this.filteredItemList.length
    },

    optionalExtensions () {
      const extensions = []

      if (this.mode === 'input' || this.mode === 'url') {
        extensions.push(new Doc())
      } else {
        extensions.push(new Bold())
        extensions.push(new Italic())
        extensions.push(new Link())
        extensions.push(new Underline())
      }
      return extensions
    },

    showSuggestions () {
      return this.query || this.hasResults
    }
  },
  data () {
    return {
      editorContent: '',
      query: '',
      editor: null,
      navigatedFieldIndex: 0,
      filteredItemList: [],
      suggestionRange: null,
      suggestionType: '',
      popup: [],
      linkUrl: '',
      linkMenuIsActive: false,
      errorMessages: [],
      linkError: ''
    }
  },

  watch: {
    content (newVal) {
      if (newVal) {
        this.editorContent = this.convertToMention(newVal)
      }
    }
  },

  methods: {

    updateContent (string, emitUpdate = false) {
      this.editor.setContent(this.convertToMention(string), emitUpdate)
    },

    focusEnd () {
      this.editor.focus('end')
    },

    dynamicFieldRegex () {
      return /<span[^>]*data-(?:mention|hashtag)-id="[^"]*"[^>]*>[\s\S]*?<\/span>/gi
    },

    hasDynamicFields (html) {
      return this.dynamicFieldRegex().test(String(html || ''))
    },

    stripDynamicFields (html) {
      return String(html || '').replace(this.dynamicFieldRegex(), '').trim()
    },

    // browsers ignore entities, whitespace and control characters when resolving a scheme
    normaliseScheme (literal) {
      const fromCode = (code) => {
        return (Number.isFinite(code) && code >= 0 && code <= 0x10ffff) ? String.fromCodePoint(code) : ''
      }
      const decoded = String(literal)
        .replace(/&amp;/gi, '&')
        .replace(/&#x([0-9a-f]+);?/gi, (match, hex) => fromCode(parseInt(hex, 16)))
        .replace(/&#(\d+);?/g, (match, dec) => fromCode(parseInt(dec, 10)))
        .replace(/&colon;/gi, ':')
        .replace(/&tab;|&newline;/gi, '')

      return Array.from(decoded)
        .filter(char => char.charCodeAt(0) > 32 && char.charCodeAt(0) !== 127)
        .join('')
        .toLowerCase()
    },

    hasExecutableScheme (literal) {
      return /^(?:javascript|data|vbscript|file|blob):/.test(this.normaliseScheme(literal))
    },

    // the field is revalidated on every keystroke, so anything merely incomplete or
    // not yet resolvable (mailto:, tel:, a dynamic field) has to survive untouched.
    // Only a scheme that can execute script is thrown away.
    mustResetUrl (html) {
      return this.hasExecutableScheme(this.stripDynamicFields(html))
    },

    isValidUrl (url) {
      let tmp = url
      if (tmp.indexOf('<p>') === 0) {
        tmp = url.split('<p>')[1].split('</p>')[0]
      }
      if (this.hasDynamicFields(tmp)) {
        const literal = this.stripDynamicFields(tmp)
        return literal === '' || /^(https?:\/\/|\/|\?|#|mailto:|tel:)/i.test(literal)
      }
      if (/^mailto:[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(tmp)) {
        return true
      }
      if (/^tel:[+\d][\d\s().-]{2,}$/i.test(tmp)) {
        return true
      }
      if (/^\/\S*$/.test(tmp)) {
        return true
      }
      return (/^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(tmp))
    },

    formatUrl (string) {
      if (!this.isValidUrl(string)) {
        this.errorMessages = [this.$t('enter_valid_url')]
      } else {
        this.errorMessages = []
      }
    },

    upHandler () {
      this.navigatedFieldIndex = ((this.navigatedFieldIndex + this.filteredItemList.length) - 1) % this.filteredItemList.length
    },

    downHandler () {
      this.navigatedFieldIndex = (this.navigatedFieldIndex + 1) % this.filteredItemList.length
    },

    enterHandler () {
      const field = this.filteredItemList[this.navigatedFieldIndex]
      if (field) {
        this.selectField(field)
      }
    },

    selectField (item) {
      const typeAttrs = {
        mention: {
          id: item.key,
          label: item.name
        },
        hashtag: {
          id: item.id,
          label: item.name
        }
      }

      this.insertMentionOrHashtag({
        range: this.suggestionRange,
        attrs: typeAttrs[this.suggestionType]
      })

      this.editor.focus()
    },

    convertToMention (string) {
      if (typeof string !== 'string') {
        return ''
      }
      const matches = string.match(/\{(.*?)\}/)

      if (matches) {
        // console.log('MATCHES', matches)
        const submatch = matches[1]
        const before = string.substr(0, string.indexOf('{'))
        const after = string.substr(string.indexOf('}') + 1)

        if (submatch.indexOf('#') > -1) {
          const fieldName = submatch.split('#')[1]
          return this.convertToMention(`${before}<span data-hashtag-id="${fieldName}">#${fieldName}</span>${after}`)
        } else {
          const field = this.findDynamicVariable(submatch)
          if (field && field.key) {
            return this.convertToMention(`${before}<span data-mention-id="${this.findDynamicVariable(submatch).key}">@${submatch}</span>${after}`)
          } else {
            return this.convertToMention(`${before}${submatch}${after}`)
          }
        }
      } else {
        return string
      }
    },

    renderPopup (node) {
      if (this.popup.length) {
        return
      }
      // ref: https://atomiks.github.io/tippyjs/v6/all-props/
      this.popup = tippy('.app', {
        interactive: true,
        sticky: true, // make sure position of tippy is updated when content changes
        plugins: [sticky],
        content: this.$refs.suggestions,
        popperOptions: {
          placement: 'auto'
        },
        // trigger: 'mouseenter', // manual
        showOnCreate: true,
        theme: 'dark',
        // placement: 'top-start',
        inertia: true,
        arrow: true,
        duration: [400, 200]
      })
    },

    filter (item, queryText, itemText) {
      const words = queryText.split(' ')
      const lastWord = words[words.length - 1]

      return item.name.toLowerCase().indexOf(lastWord.toLowerCase()) > -1
    },

    destroyPopup () {
      if (this.popup[0]) {
        this.popup[0].destroy()
        this.popup = null
      }
    },

    closeEditorMenu () {
      this.$nextTick(() => this.editor?.focus('end'))
    },

    showLinkMenu (attrs) {
      this.linkUrl = attrs.href
      this.linkError = ''
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },

    hideLinkMenu () {
      this.linkUrl = null
      this.linkError = ''
      this.linkMenuIsActive = false
    },

    // Close the hyperlink URL input (and its toolbar) when clicking anywhere outside this editor
    handleDocumentMouseDown (event) {
      if (this.linkMenuIsActive && this.$el && !this.$el.contains(event.target)) {
        this.hideLinkMenu()
        // Collapse the selection so the formatting toolbar closes along with the input
        this.editor.setSelection(this.editor.state.selection.to, this.editor.state.selection.to)
        this.editor.blur()
      }
    },

    setLinkUrl (command) {
      const msg = this.validateLinkForMenu(this.linkUrl)
      if (msg) {
        this.linkError = msg
        this.$nextTick(() => this.$refs.linkInput && this.$refs.linkInput.focus())
        return
      }
      this.linkError = ''
      command({ href: String(this.linkUrl).trim() })
      this.hideLinkMenu()
      this.closeEditorMenu()
    },

    validateLinkForMenu (rawUrl) {
      const url = String(rawUrl || '').trim()
      if (!url || !this.isValidUrl(url)) return this.$t('enter_valid_url')
      return ''
    },

    removeLink (command) {
      command({ href: null })
      this.hideLinkMenu()
      this.closeEditorMenu()
    },

    onEnterSuggestionsList ({ items, query, range, command, virtualNode }, suggestionType) {
      if (!this.editor || !this.editor.focused) return
      this.query = query
      this.filteredItemList = items
      this.suggestionType = suggestionType
      this.suggestionRange = range
      this.renderPopup(virtualNode)
      this.insertMentionOrHashtag = command
    },

    onChangeSuggestionsList ({ items, query, range, virtualNode }) {
      if (!this.editor || !this.editor.focused) return
      this.query = query
      this.filteredItemList = items
      this.suggestionRange = range
      this.navigatedFieldIndex = 0
      this.renderPopup(virtualNode)
    },

    onExitSuggestionsList () {
      this.suggestionType = ''
      this.query = null
      this.filteredItemList = []
      this.suggestionRange = null
      this.navigatedFieldIndex = 0
      this.destroyPopup()
    },

    onKeyDownSuggestionList ({ event }) {
      if (event.key === 'ArrowUp') {
        this.upHandler()
        return true
      }
      if (event.key === 'ArrowDown') {
        this.downHandler()
        return true
      }
      if (event.key === 'Enter') {
        this.enterHandler()
        return true
      }
      return false
    },

    onFilterSuggestionList (items, query) {
      return items.filter((elem) => {
        return elem.name.toLowerCase().indexOf(query) > -1
      })
    }
  },
  mounted () {
    this.editorContent = this.convertToMention(this.content)
    this.editor = new Editor({
      extensions: [
        ...this.optionalExtensions,
        new Placeholder({
          emptyEditorClass: 'is-editor-empty',
          emptyNodeClass: 'is-empty',
          emptyNodeText: this.placeholder,
          showOnlyWhenEditable: true,
          showOnlyCurrent: true
        }),
        // the landing page shows @/# variables as only plain text in some fields (e.g. the
        // footer), so those fields shouldn't offer the variable dropdown at all
        ...(this.disableVariables ? [] : [
          new Mention({
            items: () => this.dynamicVariablesAndScmFields,
            onEnter: (props) => {
              this.onEnterSuggestionsList(props, 'mention')
            },
            onChange: this.onChangeSuggestionsList,
            onExit: this.onExitSuggestionsList,
            onKeyDown: this.onKeyDownSuggestionList,
            onFilter: this.onFilterSuggestionList
          }),

          new Hashtag({
            items: () => this.productStcData,
            onEnter: (props) => {
              this.onEnterSuggestionsList(props, 'hashtag')
            },
            onChange: this.onChangeSuggestionsList,
            onExit: this.onExitSuggestionsList,
            onKeyDown: this.onKeyDownSuggestionList,
            onFilter: this.onFilterSuggestionList
          })
        ])
      ],
      content: this.editorContent,
      onUpdate: ({ getHTML, getJSON }) => {
        if (this.mode === 'url') {
          let tmp = getHTML()
          tmp = tmp.split('<p>')[1].split('</p>')[0]

          const finalSanitized = DOMPurify.sanitize(tmp)

          if (this.mustResetUrl(finalSanitized)) {
            tmp = 'https://'
            this.$nextTick(() => {
              this.editor.setContent('<p>https://</p>')
            })
            return
          }

          this.formatUrl(`<p>${tmp}</p>`)
          this.$emit('update:content', getHTML())
        } else {
          this.$emit('update:content', getHTML())
        }
        this.$emit('update:json', getJSON())
      },
      onFocus: () => {
        if (this.globalTabClasses && !this.disableInput) {
          // this.$refs['rich-input'].style.outline = '2px #15bbf0 solid'
          this.$emit('rich-text-input-focused')
        }
      },
      onBlur: () => {
        if (this.globalTabClasses && !this.disableInput) {
          this.$refs['rich-input'].style.outline = 'none'
          this.$emit('rich-text-input-blurred')
        }
      }
    })

    this.$emit('update:json', this.editor.getJSON())

    document.addEventListener('mousedown', this.handleDocumentMouseDown)
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
  }
}

</script>

<style lang="sass">

@import '@/variables.sass'

.rich-input
  background: white
  // border-radius: 4px
  .rich-label
    padding-top: 10px
    font-size: 14px
    color: $label-grey

  .error-ctn
    color: red
    font-size: 14px

.single-line
  p
    margin-bottom: 0px !important

  .ProseMirror
    min-height: 27px !important

.ProseMirror
  p.is-editor-empty:first-child::before
    content: attr(data-empty-text)
    float: left
    color: #aaa
    pointer-events: none
    height: 0
    font-style: italic

.text-editor
  min-width: auto !important
  width: 100%
  border-bottom: 1px solid $label-grey

.mention, .hashtag
  background: #dfdfdf
  border-radius: 6px
  font-family: monospace
  padding: 4px

.suggestion-list
  padding: 0.2rem
  z-index: 999
  border: 2px solid black
  color: white
  background: #333333
  font-size: 0.8rem
  font-weight: bold
  &__no-results
    padding: 0.2rem 0.5rem
  &__item
    border-radius: 5px
    padding: 0.2rem 0.5rem
    margin-bottom: 0.2rem
    cursor: pointer
    &:last-child
      margin-bottom: 0
    &.is-selected,
    &:hover
      background-color: rgba(white, 0.2)
    &.is-empty
      opacity: 0.5

.editor__content.disabled
  pointer-events: none
  cursor: unset

.menububble__form
  flex-direction: column

.menububble__error
  align-self: start
  margin-top: 6px
  font-size: 12px
  color: #ff6b6b
</style>
